import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, X, Vote, Lightbulb, Loader2 } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

export const AdminProposalsTab = ({ pendingProposals, token, onRefresh }) => {
  const [processingProposal, setProcessingProposal] = useState(null);

  const handleProposalAction = async (proposalId, action) => {
    setProcessingProposal(proposalId);
    try {
      await axios.post(`${API}/voting/proposals/${proposalId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Proposal ${action}d successfully`);
      onRefresh();
    } catch (error) {
      toast.error(`Failed to ${action} proposal`);
    } finally {
      setProcessingProposal(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="glass-card rounded-2xl border-0">
        <CardContent className="p-6">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-teal-500" />
            Pending Feature Proposals
          </h2>

          {pendingProposals.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Vote className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No proposals pending review</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingProposals.map((proposal) => (
                <div key={proposal.id} className="bg-muted/30 rounded-xl p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          proposal.category === 'feature' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' :
                          proposal.category === 'improvement' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                          'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>
                          {proposal.category}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          by {proposal.submitted_by_name}
                        </span>
                      </div>
                      <h3 className="font-semibold text-foreground text-lg mb-2">
                        {proposal.title}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {proposal.description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Submitted {new Date(proposal.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleProposalAction(proposal.id, 'approve')}
                        disabled={processingProposal === proposal.id}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-lg"
                      >
                        {processingProposal === proposal.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleProposalAction(proposal.id, 'reject')}
                        disabled={processingProposal === proposal.id}
                        className="text-red-600 border-red-300 hover:bg-red-50 rounded-lg"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
